import React, { useState, useEffect, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { Box, Typography, Container, Paper, Tabs, Tab } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";

import TabPanel from "../../components/TabPanel";

import Campaigns from "../Campaigns";
import ContactLists from "../ContactLists";
import CampaignsConfig from "../CampaignsConfig";
import CampaignsPhrase from "../CampaignsPhrase";
import FlowBuilder from "../FlowBuilder";
import { 
  CampaignOutlined, 
  AccountTreeOutlined 
} from "@mui/icons-material";
import { alpha } from "@mui/material";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";

const useStyles = makeStyles((theme) => ({
  tabContainer: {
    backgroundColor: "transparent",
    padding: theme.spacing(0, 3),
    boxSizing: "border-box",
  },
  tab: {
    fontWeight: "bold",
    textTransform: "none",
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    padding: theme.spacing(3),
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "transparent",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
}));

const AutomationCustom = () => {
  const classes = useStyles();
  const themeV4 = useThemeV4();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeV4.palette.type,
          primary: themeV4.palette.primary,
          secondary: themeV4.palette.secondary,
          error: themeV4.palette.error,
          warning: themeV4.palette.warning,
          info: themeV4.palette.info,
          success: themeV4.palette.success,
          text: themeV4.palette.text,
          background: themeV4.palette.background,
          divider: themeV4.palette.divider,
        },
        shape: {
          borderRadius: themeV4.shape.borderRadius,
        },
        typography: {
          fontFamily: themeV4.typography.fontFamily,
        },
      }),
    [themeV4],
  );

  const [tab, setTab] = useState("campaigns");
  const [showCampaigns, setShowCampaigns] = useState(false);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        setShowCampaigns(planConfigs.plan.useCampaigns);
      } catch (e) {
        toast.error(e);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "light" ? "#f5f5f5" : "background.default",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          p: 3,
        }}
      >
        <Container maxWidth="xl" disableGutters>
          {user.profile === "user" ? (
            <ForbiddenPage />
          ) : (
            <>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight="800"
                    color="primary"
                    gutterBottom
                  >
                    Automação
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Gerencie campanhas e fluxos de forma centralizada
                  </Typography>
                </Box>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  bgcolor: "transparent",
                }}
              >
                <div className={classes.tabContainer}>
                  <Tabs
                    value={tab}
                    indicatorColor="primary"
                    textColor="primary"
                    scrollButtons="auto"
                    variant="scrollable"
                    onChange={handleTabChange}
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: "50px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 2,
                      minHeight: "60px",
                      "& .MuiTabs-flexContainer": {
                        justifyContent: "flex-start",
                        gap: "8px",
                        px: 1,
                        py: 0.5,
                      },
                      "& .MuiTab-root": {
                        fontWeight: "600",
                        textTransform: "none",
                        minHeight: "48px",
                        px: 3,
                        borderRadius: "50px",
                        color: "text.secondary",
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.text.primary, 0.05),
                          color: "primary.main",
                        },
                      },
                      "& .Mui-selected": {
                        color: "primary.main !important",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      },
                      "& .MuiTabs-indicator": {
                        display: "none",
                      },
                    }}
                  >
                    {showCampaigns && (
                      <Tab
                        className={classes.tab}
                        icon={<CampaignOutlined sx={{ fontSize: 20 }} />}
                        iconPosition="start"
                        label="Campanhas"
                        value={"campaigns"}
                      />
                    )}
                    <Tab
                      className={classes.tab}
                      icon={<AccountTreeOutlined sx={{ fontSize: 20 }} />}
                      iconPosition="start"
                      label="Fluxo de Conversa"
                      value={"flowbuilders"}
                    />
                  </Tabs>
                </div>
                <div className={classes.paper}>
                  {showCampaigns && (
                    <TabPanel
                      className={classes.container}
                      value={tab}
                      name={"campaigns"}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                        <Campaigns />
                        <ContactLists />
                        <CampaignsConfig />
                      </Box>
                    </TabPanel>
                  )}
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"flowbuilders"}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3}}>
                      <FlowBuilder />
                      <CampaignsPhrase />
                    </Box>
                  </TabPanel>
                </div>
              </Paper>
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AutomationCustom;
